<template>
  <div class="max-w-7xl mx-auto px-4 py-8 font-inter">
    <div class="flex items-center justify-between mb-6">
      <button @click="$router.push(backLink)" class="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900">
        <ArrowLeft class="h-4 w-4" />
        {{ backLabel }}
      </button>
      <div class="flex gap-2">
        <button
          v-if="profile.has_resume"
          @click="downloadResume"
          class="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-teal-700 transition"
        >
          <Download class="h-4 w-4" />
          Download Resume
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
      <!-- Left: Profile Overview -->
      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div class="flex items-start gap-4">
            <div class="h-20 w-20 rounded-full overflow-hidden border border-gray-200 bg-gray-100">
              <img :src="profilePicture" alt="Profile picture" class="h-full w-full object-cover" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h1 class="text-2xl font-semibold text-gray-900 leading-tight">{{ fullName }}</h1>
              </div>
              <p class="text-sm text-gray-600 mt-1">{{ profile.current_role || 'Role not specified' }}</p>
              <div class="flex flex-wrap items-center gap-2 text-sm text-gray-600 mt-2">
                <span class="inline-flex items-center gap-1">
                  <MapPin class="h-4 w-4 text-teal-600" />
                  {{ locationDisplay }}
                </span>
                <span v-if="profile.current_company" class="inline-flex items-center gap-1">
                  <Building2 class="h-4 w-4 text-indigo-600" />
                  {{ profile.current_company }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex flex-wrap gap-2">
            <span v-if="availabilityDisplay" class="px-3 py-1 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
              {{ availabilityDisplay }}
            </span>
            <span v-if="rateDisplay" class="px-3 py-1 text-xs font-semibold rounded-full bg-sky-50 text-sky-700 border border-sky-100">
              {{ rateDisplay }}
            </span>
            <span class="px-3 py-1 text-xs font-semibold rounded-full bg-purple-50 text-purple-700 border border-purple-100" v-if="profile.visa_status">
              {{ profile.visa_status }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 mt-6 text-sm">
          <div class="p-3 border border-gray-200 rounded-lg bg-gray-50/60 min-h-[82px]">
            <p class="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-semibold">Experience</p>
            <p class="text-base font-semibold text-gray-900 mt-1">{{ totalExperience }}</p>
          </div>
          <div class="p-3 border border-gray-200 rounded-lg bg-gray-50/60 min-h-[82px]">
            <p class="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-semibold">Role Tenure</p>
            <p class="text-base font-semibold text-gray-900 mt-1">{{ roleExperience }}</p>
          </div>
          <div class="p-3 border border-gray-200 rounded-lg bg-gray-50/60 min-h-[82px]">
            <p class="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-semibold">Company Tenure</p>
            <p class="text-base font-semibold text-gray-900 mt-1">{{ companyExperience }}</p>
          </div>
          <div class="p-3 border border-gray-200 rounded-lg bg-gray-50/60 min-h-[82px]">
            <p class="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-semibold">Contract Type</p>
            <p class="text-base font-semibold text-gray-900 mt-1">{{ contractTypeDisplay }}</p>
          </div>
        </div>
      </div>

      <!-- Right: Contact + Quick facts -->
      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
        <div class="flex items-center gap-3">
          <div class="h-10 w-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center font-semibold text-sm border border-teal-100">
            <Mail class="h-5 w-5" />
          </div>
          <div>
            <p class="text-xs text-gray-500 font-semibold">Email</p>
            <p class="text-sm font-medium text-gray-800 break-all">{{ profile.email || 'N/A' }}</p>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <div class="h-10 w-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center font-semibold text-sm border border-indigo-100">
            <Phone class="h-5 w-5" />
          </div>
          <div>
            <p class="text-xs text-gray-500 font-semibold">Phone</p>
            <p class="text-sm font-medium text-gray-800">{{ profile.phone || 'N/A' }}</p>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <div class="h-10 w-10 rounded-full bg-orange-50 text-orange-700 flex items-center justify-center font-semibold text-sm border border-orange-100">
            <Briefcase class="h-5 w-5" />
          </div>
          <div>
            <p class="text-xs text-gray-500 font-semibold">Ideal role</p>
            <p class="text-sm font-medium text-gray-800">{{ profile.ideal_role || profile.role || 'Not specified' }}</p>
          </div>
        </div>
        <div class="flex items-center gap-3" v-if="profile.linkedin_url">
          <div class="h-10 w-10 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center font-semibold text-sm border border-sky-100">
            <BadgeCheck class="h-5 w-5" />
          </div>
          <div>
            <p class="text-xs text-gray-500 font-semibold">LinkedIn</p>
            <a :href="profile.linkedin_url" target="_blank" class="text-sm font-medium text-sky-700 hover:underline">
              {{ profile.linkedin_url }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div class="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Career Snapshot</h3>
        <div class="grid sm:grid-cols-2 gap-4 text-sm">
          <div v-if="profile.current_company">
            <p class="text-xs font-semibold text-gray-500 mb-1">Current company</p>
            <p class="text-gray-800">{{ profile.current_company }}</p>
          </div>
          <div v-if="companiesDisplay">
            <p class="text-xs font-semibold text-gray-500 mb-1">Past companies</p>
            <div class="flex flex-wrap gap-1">
              <span v-for="company in companiesDisplay.slice(0,4)" :key="company" class="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full border border-gray-200">
                {{ company }}
              </span>
              <span v-if="companiesDisplay.length > 4" class="px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded-full border border-gray-200">
                +{{ companiesDisplay.length - 4 }} more
              </span>
            </div>
          </div>
          <div v-if="profile.industry">
            <p class="text-xs font-semibold text-gray-500 mb-1">Industry</p>
            <p class="text-gray-800">{{ profile.industry }}</p>
          </div>
          <div v-if="profile.role">
            <p class="text-xs font-semibold text-gray-500 mb-1">Role</p>
            <p class="text-gray-800">{{ profile.role }}</p>
          </div>
          <div v-if="workTravelDisplay">
            <p class="text-xs font-semibold text-gray-500 mb-1">Work travel</p>
            <p class="text-gray-800">{{ workTravelDisplay }}</p>
          </div>
          <div v-if="salaryDisplay">
            <p class="text-xs font-semibold text-gray-500 mb-1">Salary</p>
            <p class="text-gray-800">{{ salaryDisplay }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Availability</h3>
        <div class="flex items-center gap-2 text-sm text-gray-700">
          <Clock class="h-4 w-4 text-teal-600" />
          <span>{{ availabilityDisplay }}</span>
        </div>
        <div class="mt-4">
          <h4 class="text-xs font-semibold text-gray-500 mb-1">Visa status</h4>
          <p class="text-sm text-gray-800">{{ profile.visa_status || 'Not provided' }}</p>
        </div>
      </div>
    </div>

    <div class="mt-6 bg-white border border-gray-200 rounded-xl shadow-sm p-6" v-if="questions.length > 0 && profile.answers">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Screening Answers</h3>
      <div class="space-y-4">
        <div v-for="(item, key) in questions" :key="item.id" class="border border-gray-100 rounded-lg p-4 bg-gray-50/60">
          <p class="text-sm font-semibold text-gray-900">{{ item.question }}</p>
          <p v-if="profile.answers[key]" class="text-sm text-gray-700 mt-1">{{ profile.answers[key].response }}</p>
          <p v-else class="text-sm text-gray-400 mt-1">No response provided.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import genericService from '@/services/marketplace/generic.service';
import companyService from '@/services/marketplace/company.service'
import { ArrowLeft, Download, MapPin, Building2, Mail, Phone, BadgeCheck, Briefcase, Clock } from 'lucide-vue-next';

export default {
  name: 'CompanyCandidateView',
  inject: ['toggleLoading'],
  data() {
    return {
      profile: {},
      questions: [],
      profilePicture: 'https://via.placeholder.com/128'
    };
  },
  computed: {
    isPoolRoute() {
      return Boolean(this.$route.params.poolId);
    },
    backLink() {
      return this.isPoolRoute
        ? `/company/talent-pools/${this.$route.params.poolId}`
        : `/company/view-job/${this.$route.params.jobId}`;
    },
    backLabel() {
      return this.isPoolRoute ? 'Back to Talent Pool' : 'Back to Job';
    },
    rateDisplay() {
      if (this.profile.salary) return this.formatMoney(this.profile.salary, true);
      if (this.profile.rate) {
        const rate = this.formatMoney(this.profile.rate);
        return `${rate} / ${this.profile.rate_type || 'rate'}`;
      }
      return '';
    },
    contractTypeDisplay() {
      return this.profile.contract_type || 'Not specified';
    },
    fullName() {
      return this.profile.full_name || `${this.profile.first_name || ''} ${this.profile.last_name || ''}`.trim() || 'Contractor';
    },
    locationDisplay() {
      const parts = [this.profile.suburb, this.profile.state].filter(Boolean);
      return parts.length ? parts.join(', ') : 'Location not specified';
    },
    availabilityDisplay() {
      return this.$filters.screenedAvailability
        ? this.$filters.screenedAvailability(this.profile.availability)
        : (this.profile.availability || 'Not specified');
    },
    totalExperience() {
      return this.formatMonths(this.profile.total_experience);
    },
    roleExperience() {
      return this.formatMonths(this.profile.experience_role);
    },
    companyExperience() {
      return this.formatMonths(this.profile.experience_company);
    },
    companiesDisplay() {
      let companies = this.profile.companies;
      if (typeof companies === 'string') {
        try {
          companies = JSON.parse(companies);
        } catch (e) {
          companies = companies.split(',').map(c => c.trim()).filter(Boolean);
        }
      }
      if (!Array.isArray(companies) || !companies.length) return [];
      return companies.map(c => String(c).trim()).filter(Boolean);
    },
    salaryDisplay() {
      if (!this.profile.salary) return '';
      return this.formatMoney(this.profile.salary, true);
    },
    workTravelDisplay() {
      const v = String(this.profile.work_travel || '').toUpperCase();
      if (!v) return '';
      if (v === 'Y' || v === 'YES' || v === 'TRUE' || v === '1') return 'Yes';
      if (v === 'N' || v === 'NO' || v === 'FALSE' || v === '0') return 'No';
      return v;
    }
  },
  methods: {
    formatMoney(value, isSalary = false) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '';
      const formatted = num >= 1000 ? `$${num.toLocaleString()}` : `$${num}`;
      return isSalary ? `${formatted}` : formatted;
    },
    formatMonths(value) {
      if (value === undefined || value === null || isNaN(value)) return 'N/A';
      const months = Math.round(Number(value));
      if (months < 12) return `${months} mo${months === 1 ? '' : 's'}`;
      const years = Math.floor(months / 12);
      const rem = months % 12;
      return rem ? `${years} yr ${rem} mo` : `${years} yr`;
    },
    async getContractorProfile() {
      this.toggleLoading(true);

      const isPool = this.isPoolRoute;
      const request = isPool
        ? companyService.getTalentPoolMemberProfile(this.$route.params.poolId, this.$route.params.profile_hash)
        : companyService.getApplicantProfile(this.$route.params.jobId, this.$route.params.profile_hash);

      request
        .then(response => {
          this.profile = response.data
        })
        .catch((error) => {
          // this.$router.push('/dashboard/auth');
        })
        .finally(async() => {
          this.profilePicture = genericService.getProfilePictureLink(this.profile.photo_hash, this.profile.profile_picture_s3_key)
          const response = await genericService.getQuestions(this.profile.role_id);
          this.questions = response.data;

          this.toggleLoading(false);
        })
    },
    async downloadResume() {
      if (!this.profile?.id) return;
      this.toggleLoading(true);
      try {
        const response = this.isPoolRoute
          ? await companyService.downloadPoolMemberResume(this.$route.params.poolId, this.profile.id)
          : await companyService.downloadApplicantResume(this.$route.params.jobId, this.profile.profile_hash);

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.fullName || 'Resume'}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error(e);
      } finally {
        this.toggleLoading(false);
      }
    }
  },
  created() {
    this.getContractorProfile()
  },
  components: {
    ArrowLeft,
    Download,
    MapPin,
    Building2,
    Mail,
    Phone,
    BadgeCheck,
    Briefcase,
    Clock
  }
}
</script>
